import React, { useMemo, useState, useEffect } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { useAuth } from "@/context/AuthContext";
import { INTERACTIVE_STAGES, COMPLETED_STAGES, SCENARIO } from "@/lib/demoCatalog";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, Circle, ArrowRight, Users, LogIn, PlayCircle, Eye } from "lucide-react";

const MODES = {
  interactive: { stages: INTERACTIVE_STAGES, storeKey: "demo_lifecycle_stage_interactive" },
  completed: { stages: COMPLETED_STAGES, storeKey: "demo_lifecycle_stage_completed" },
};

export default function DemoLifecycle() {
  const config = usePublicConfig();
  const { login, demoLogin } = useAuth();
  // keep `login` for the email/password form; personas use passwordless demoLogin
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "completed" ? "completed" : "interactive";
  const { stages, storeKey } = MODES[mode];

  const [idx, setIdx] = useState(() => Number(sessionStorage.getItem(storeKey) || 0));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setIdx(Number(sessionStorage.getItem(storeKey) || 0)); }, [storeKey]);
  useEffect(() => { sessionStorage.setItem(storeKey, String(idx)); }, [idx, storeKey]);

  const personas = useMemo(() => {
    const map = {};
    (config?.demo?.personas || []).forEach((p) => { map[p.key] = p; });
    return map;
  }, [config]);

  if (config === null) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!config?.demo?.enabled) return <Navigate to="/login" replace />;

  const stage = stages[Math.min(idx, stages.length - 1)];
  const persona = personas[stage.persona];
  const isCompleted = mode === "completed";

  const switchMode = (m) => { setParams(m === "completed" ? { mode: "completed" } : {}); };

  const enterStage = async () => {
    if (!persona) { toast.error("This persona isn't available in the demo config."); return; }
    setBusy(true);
    try {
      await demoLogin(persona.key);
      toast.success(`Signed in as ${persona.label}`);
      navigate(stage.route);
    } catch {
      toast.error("Could not start this stage. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="demo-lifecycle">
      <header className="h-14 bg-white border-b border-brand-line flex items-center px-6">
        <Link to="/demo" className="font-display font-extrabold text-brand-ink">GovFlowState <span className="text-xs font-semibold text-slate-400">Flagship Demo</span></Link>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1" data-testid="sample-data-badge">
          <ShieldCheck className="h-3.5 w-3.5" /> SAMPLE DATA
        </span>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Mode toggle */}
        <div className="inline-flex rounded-xl border border-brand-line bg-white p-1 mb-6" data-testid="demo-mode-toggle">
          <button onClick={() => switchMode("interactive")} data-testid="demo-mode-interactive"
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${!isCompleted ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-brand-bg"}`}>
            <PlayCircle className="h-4 w-4" /> Interactive Walkthrough
          </button>
          <button onClick={() => switchMode("completed")} data-testid="demo-mode-completed"
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${isCompleted ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-brand-bg"}`}>
            <Eye className="h-4 w-4" /> Completed Example
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Progress / stage list */}
          <aside className="lg:col-span-1">
            <h2 className="font-display font-bold text-brand-ink mb-1">{SCENARIO.contractId}</h2>
            <p className="text-xs text-slate-500 mb-1">{SCENARIO.title}</p>
            <p className="text-xs text-slate-500 mb-4">
              {isCompleted
                ? "A finished, read-only legislative record. Inspect each stage of the completed scenario."
                : "A Parks security-camera contract travels the full legislative lifecycle. ~12 steps, 10–15 min of real actions."}
            </p>
            <div className="text-[11px] font-bold text-slate-400 mb-2">Progress: {idx + 1} / {stages.length}</div>
            <ol className="space-y-1" data-testid="demo-stage-list">
              {stages.map((s, i) => {
                const Icon = i < idx ? CheckCircle2 : i === idx ? ArrowRight : Circle;
                return (
                  <li key={s.id}>
                    <button onClick={() => setIdx(i)} data-testid={`demo-stage-${s.id}`}
                      className={`w-full text-left flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${i === idx ? "bg-brand-navy text-white" : "hover:bg-white text-slate-600"}`}>
                      <Icon className={`h-4 w-4 shrink-0 ${i < idx ? "text-brand-teal" : ""}`} />
                      <span className="truncate">{i + 1}. {s.title}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Current stage */}
          <main className="lg:col-span-2">
            <div className="rounded-2xl bg-white border border-brand-line p-6" data-testid="demo-current-stage">
              <div className="flex items-center gap-2">
                <div className="text-xs font-bold uppercase tracking-wider text-brand-civic">Stage {idx + 1} of {stages.length}</div>
                {isCompleted && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-brand-teal/10 text-brand-teal text-[11px] font-bold px-2 py-0.5" data-testid="demo-readonly-badge">
                    <Eye className="h-3 w-3" /> READ-ONLY
                  </span>
                )}
              </div>
              <h1 className="font-display text-2xl font-extrabold text-brand-ink mt-1">{stage.title}</h1>
              {persona && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-bg border border-brand-line px-3 py-1 text-sm" data-testid="demo-stage-persona">
                  <Users className="h-4 w-4 text-brand-civic" /> {isCompleted ? "View as" : "Acting as"} <strong className="text-brand-ink">{persona.label}</strong>
                  <span className="text-xs text-slate-400">({persona.role_label})</span>
                </div>
              )}
              <p className="text-slate-600 mt-4">{stage.narrative}</p>
              <div className="mt-4 rounded-lg bg-brand-civic-50 border border-brand-civic/20 p-3 text-sm text-brand-ink">
                <span className="font-semibold">{isCompleted ? "Inspect:" : "Do this:"}</span> {stage.action}
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button onClick={enterStage} disabled={busy} data-testid="demo-enter-stage"
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-navy text-white font-semibold px-5 py-2.5 hover:bg-brand-navy-900 disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                  {isCompleted ? "Inspect as" : "Enter as"} {persona?.label || stage.persona} →
                </button>
                <button onClick={() => setIdx(Math.min(idx + 1, stages.length - 1))} disabled={idx >= stages.length - 1}
                  data-testid="demo-next-stage" className="inline-flex items-center gap-2 rounded-lg border border-brand-line font-semibold px-5 py-2.5 text-brand-navy hover:bg-white disabled:opacity-40">
                  Next stage <ArrowRight className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-4">Tip: return here any time at <code className="text-brand-civic">/demo/legislative-lifecycle</code> — your progress is saved per mode.</p>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
